import { gql, useMutation, useQuery } from '@apollo/client';
import AddBankAccountModal from '@components/modals/addBankAccountModal';
import ConfirmationModal from '@components/modals/confirmationModal';
import type { Account } from '@interfaces/account';
import type { BankAccount } from '@interfaces/bankAccount';
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { colors } from '@ovComponents/legacyColor';
import { usePermission } from '@providers/userContextProvider';
import {
  filterBankAccountsByCurrency,
  generateBankDetailString,
} from '@utils/common';
import { IconButton } from 'hephaestus/web';
import { DeleteForever as DeleteForeverIcon } from 'hephaestus/webIcons';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FETCH_BANK_ACCOUNTS = gql`
  query fetchBankAccounts($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        bankAccounts {
          id
          name
          bankAccountNumber
          institutionNumber
          currency
        }
      }
    }
  }
`;

const DELETE_BANK_ACCOUNT = gql`
  mutation deleteBankAccount($bankAccountId: ObjectID!) {
    deleteBankAccount(bankAccountId: $bankAccountId) {
      bankAccount {
        id
        name
      }
    }
  }
`;

type BankAccountSelectProps = {
  userId: string;
  setBankAccount: (event: string) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium' | undefined;
  disabled?: boolean;
  readonly?: boolean;
  canEditBankAccount?: boolean;
  account?: Account | undefined;
  currency?: string;
};

export const BankAccountSelect = ({
  userId,
  setBankAccount,
  value,
  label,
  size,
  disabled = false,
  readonly = false,
  canEditBankAccount = true,
  account = undefined,
  currency,
}: BankAccountSelectProps) => {
  const { t } = useTranslation(['components']);
  const canReadBankAccount = usePermission('read:bank_account_basic');
  const canWriteBankAccount = usePermission('write:bank_account_basic');
  const [addBankAccount, setAddBankAccount] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const selectedBankAccountId = useRef<string>('');
  const { data, loading, refetch } = useQuery(FETCH_BANK_ACCOUNTS, {
    skip: userId === '' || !canReadBankAccount,
    variables: { userId },
  });
  const bankAccounts = useMemo(() => {
    const list = data?.fetchUser?.user?.bankAccounts;
    if (!list) return undefined;
    const filtered = filterBankAccountsByCurrency(list, account, currency);
    if (!value || filtered.some((x: BankAccount) => x.id === value))
      return filtered;
    const selected = list.find((x: BankAccount) => x.id === value);
    return selected ? [selected, ...filtered] : filtered;
  }, [data, account, currency, value]);

  const [deleteBankAccountMutation] = useMutation(DELETE_BANK_ACCOUNT, {
    variables: { bankAccountId: selectedBankAccountId.current },
    onCompleted: () => refetch(),
  });

  const deleteBankAccount = () => {
    deleteBankAccountMutation().then();
    setOpenConfirmationModal(false);
  };

  if (!canReadBankAccount) return null;

  return (
    <>
      <FormControl fullWidth>
        <InputLabel id='choose-bank'>{label}</InputLabel>
        <Select
          value={bankAccounts ? value : ''}
          labelId='choose-bank'
          data-testid='bankAccount-dropdown'
          input={<OutlinedInput label={label} />}
          fullWidth
          onChange={(e) => {
            if (e.target.value === 'addBank') {
              setAddBankAccount(true);
            } else {
              setAddBankAccount(false);
              setBankAccount(e.target.value);
            }
          }}
          size={size}
          disabled={disabled}
          inputProps={{ readOnly: readonly }}
          renderValue={(selected) => {
            const foundBankAccount = bankAccounts?.find(
              (item: Record<string, unknown>) => item.id === selected,
            );
            if (foundBankAccount) {
              return generateBankDetailString(foundBankAccount);
            }
            return '';
          }}
        >
          {loading ? (
            <MenuItem>...</MenuItem>
          ) : (
            (bankAccounts || [])?.map((x: any) => (
              <MenuItem
                onClick={() => setBankAccount(x.id)}
                key={x.id}
                value={x.id}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                {x.name} {x.bankAccountNumber}
                {canEditBankAccount && canWriteBankAccount ? (
                  <IconButton
                    aria-label='comment'
                    onClick={() => {
                      selectedBankAccountId.current = x.id;
                      setOpenConfirmationModal(true);
                    }}
                  >
                    <DeleteForeverIcon fontSize='inherit' />
                  </IconButton>
                ) : undefined}
              </MenuItem>
            ))
          )}
          {canEditBankAccount && canWriteBankAccount && (
            <MenuItem
              sx={{ color: colors.success }}
              key='addBank'
              value='addBank'
            >
              {t('components:bankAccountSelect.addBank')}
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {canEditBankAccount && (
        <AddBankAccountModal
          handleClose={() => setAddBankAccount(false)}
          open={addBankAccount}
          afterCreate={(bank) => {
            setBankAccount(bank.id);
            refetch();
            setAddBankAccount(false);
          }}
          userId={userId}
        />
      )}
      <ConfirmationModal
        open={openConfirmationModal}
        title={t('components:bankAccountSelect.confirmationDialogTitle')}
        onCancel={() => setOpenConfirmationModal(false)}
        onConfirm={() => deleteBankAccount()}
      />
    </>
  );
};

export const testables = {
  FETCH_BANK_ACCOUNTS,
};
